import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateVideoRenderDto,
  DeleteVideoRenderFromVimeoResponseDto,
  VideoRenderDto,
} from "@repo/api/video-render";
import { VideoRenderApi } from "../api/video-render-api";

export function useVideoRendersForVideoQuery(
  teamExternalId: string,
  videoExternalId: string,
) {
  return useQuery<VideoRenderDto[]>({
    queryKey: videoRenderQueryKeys.getVideoRendersForVideo(
      teamExternalId,
      videoExternalId,
    ),
    queryFn: () =>
      VideoRenderApi.getVideoRenders(teamExternalId, videoExternalId),
    refetchInterval: (query) => {
      const data = query.state.data;

      if (!data) {
        return false;
      }

      const isRendering = data.some((render) =>
        ["rendering", "pending", "upscaling"].includes(render.status),
      );

      const isSharePending = data.some(
        (render) => render.shareInfo?.status === "pending",
      );

      return isRendering || isSharePending ? 3000 : false;
    },
  });
}

export function useCreateVideoRenderMutation({
  teamExternalId,
  videoExternalId,
  onMutate,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  videoExternalId: string;
  onMutate?: (data: CreateVideoRenderDto) => void;
  onSuccess?: (data: VideoRenderDto, variables: CreateVideoRenderDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: videoRenderQueryKeys.createVideoRender(
      teamExternalId,
      videoExternalId,
    ),
    mutationFn: (data: CreateVideoRenderDto) =>
      VideoRenderApi.createVideoRender(teamExternalId, videoExternalId, data),
    onMutate,
    onSuccess,
    onError,
  });
}

export function useDeleteVideoRenderFromVimeoMutation({
  teamExternalId,
  videoRenderExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  videoRenderExternalId: string;
  onSuccess?: (data: DeleteVideoRenderFromVimeoResponseDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: videoRenderQueryKeys.deleteVideoRenderFromVimeo(
      teamExternalId,
      videoRenderExternalId,
    ),
    mutationFn: () =>
      VideoRenderApi.deleteVideoRenderFromVimeo(
        teamExternalId,
        videoRenderExternalId,
      ),
    onSuccess,
    onError,
  });
}

function useUpdateVideoRenderInCache(
  teamExternalId: string,
  videoExternalId: string,
) {
  const queryClient = useQueryClient();

  return (updated: VideoRenderDto) => {
    queryClient.setQueryData<VideoRenderDto[]>(
      videoRenderQueryKeys.getVideoRendersForVideo(
        teamExternalId,
        videoExternalId,
      ),
      (old) =>
        old?.map((render) =>
          render.externalId === updated.externalId ? updated : render,
        ),
    );
  };
}

export function useShareVideoRenderMutation({
  teamExternalId,
  videoExternalId,
  onMutate,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  videoExternalId: string;
  onMutate?: (videoRenderExternalId: string) => void;
  onSuccess?: (data: VideoRenderDto) => void;
  onError?: (error: Error) => void;
}) {
  const updateCache = useUpdateVideoRenderInCache(
    teamExternalId,
    videoExternalId,
  );

  return useMutation({
    mutationKey: videoRenderQueryKeys.shareVideoRender(teamExternalId),
    mutationFn: (videoRenderExternalId: string) =>
      VideoRenderApi.shareVideoRender(teamExternalId, videoRenderExternalId),
    onMutate,
    onSuccess: (data) => {
      updateCache(data);
      onSuccess?.(data);
    },
    onError,
  });
}

export function useDeleteVideoRenderShareMutation({
  teamExternalId,
  videoExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  videoExternalId: string;
  onSuccess?: (data: VideoRenderDto) => void;
  onError?: (error: Error) => void;
}) {
  const updateCache = useUpdateVideoRenderInCache(
    teamExternalId,
    videoExternalId,
  );

  return useMutation({
    mutationKey: videoRenderQueryKeys.deleteVideoRenderShare(teamExternalId),
    mutationFn: (videoRenderExternalId: string) =>
      VideoRenderApi.deleteVideoRenderShare(
        teamExternalId,
        videoRenderExternalId,
      ),
    onSuccess: (data) => {
      updateCache(data);
      onSuccess?.(data);
    },
    onError,
  });
}

export const videoRenderQueryKeys = {
  getVideoRendersForVideo: (
    teamExternalId: string,
    videoExternalId: string,
  ) => ["videoRender", { teamExternalId, videoExternalId }],
  createVideoRender: (teamExternalId: string, videoExternalId: string) => [
    "videoRender",
    "create",
    { teamExternalId, videoExternalId },
  ],
  deleteVideoRenderFromVimeo: (
    teamExternalId: string,
    videoRenderExternalId: string,
  ) => [
    "videoRender",
    "vimeo",
    "delete",
    { teamExternalId, videoRenderExternalId },
  ],
  shareVideoRender: (teamExternalId: string) => [
    "videoRender",
    "share",
    "create",
    { teamExternalId },
  ],
  deleteVideoRenderShare: (teamExternalId: string) => [
    "videoRender",
    "share",
    "delete",
    { teamExternalId },
  ],
};
